import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { Options, UseAxios, makeUseAxios } from "axios-hooks";
import { stringify } from "query-string";

import config from "config";

import { useApiRequest } from "./interceptors";
import { ApiError } from "./shared";

interface Api {
  instance: AxiosInstance;
  useAxios: UseAxios;
}

const portalInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.API_URL,
  paramsSerializer: (params) => stringify(params),
});

const publicInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.PUBLIC_API_URL,
});

const productsInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.PRODUCTS_API_URL,
});

const usersInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.USER_MANAGEMENT_API_URL,
});

const api = {
  portal: {
    instance: portalInstance,
    useAxios: makeUseAxios({
      axios: portalInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
  public: {
    instance: publicInstance,
    useAxios: makeUseAxios({
      axios: publicInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
  products: {
    instance: productsInstance,
    useAxios: makeUseAxios({
      axios: productsInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
  users: {
    instance: usersInstance,
    useAxios: makeUseAxios({
      axios: usersInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
};

const makeUseApiRequest =
  (useAxios: UseAxios, serviceName: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <TResponse, TError extends ApiError, TBody = any>(
    requestConfig: AxiosRequestConfig | string,
    options?: Options
  ) => {
    return useApiRequest<TResponse, TBody, TError>(
      useAxios,
      serviceName,
      requestConfig,
      options
    );
  };

export const usePortalApi = makeUseApiRequest(api.portal.useAxios, "PortalApi");

export const usePublicApi = makeUseApiRequest(api.public.useAxios, "PublicApi");

export const useProductsApi = makeUseApiRequest(
  api.products.useAxios,
  "ProductsApi"
);

export const useUsersApi = makeUseApiRequest(api.users.useAxios, "UsersApi");

export default api;
